import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { VerificacionService } from 'src/app/comun/services/verificacion.service';
import { MicroempresarioService } from 'src/app/microempresario/services/microempresario.service';
import { HomeCuentaCampaniaService } from './home-cuenta-campania.service';
import { UtmTrackingService } from 'src/app/services/utm-tracking.service';

export interface HomeCuentaFormValueBase {
  primerNombre: string;
  segundoNombre?: string;
  primerApellido: string;
  segundoApellido?: string;
  celular: string;
  correo: string;
  tipoDoc: string | number;
  numDoc: string;
  password: string;
  contacto: string | number | Array<string | number>;
  origen: string | number;
}

export interface HomeCuentaNaturalFormValue extends HomeCuentaFormValueBase {
  tieneRUT: string;
}

export interface HomeCuentaJuridicaFormValue extends HomeCuentaFormValueBase {
  nit: string;
  dv?: string | number;
  personaDeContacto?: string;
  tipoPersonaOtro?: string;
}

@Injectable({ providedIn: 'root' })
export class HomeCuentaRegistroService {
  constructor(
    private readonly microempresario: MicroempresarioService,
    private readonly verificacion: VerificacionService,
    private readonly router: Router,
    private readonly campania: HomeCuentaCampaniaService,
    private readonly utmTracking: UtmTrackingService
  ) {}

  registrarNatural(form: HomeCuentaNaturalFormValue, tratamientoDatos: boolean): Observable<any> {
    this.utmTracking.registrarInicioRegistro();
    const payload = this.buildNaturalPayload(form, tratamientoDatos);
    return this.microempresario.savePersonaNatural(payload).pipe(
      map((data: any) => {
        this.assertRegistroExitoso(data);
        this.campania.clearExtras();
        this.utmTracking.registrarRegistroExitoso(
          {
            numeroDocumento: String(payload.numeroDocumento ?? ''),
            correoElectronico: String(payload.correoElectronico ?? ''),
            razonSocial: `${payload.primerNombre || ''} ${payload.primerApellido || ''}`.trim(),
          },
          Number(data?.idUsuario) || undefined
        );
        this.guardarSesionYNavegarOtp(
          data,
          String(payload.correoElectronico),
          {
            idEmpresario: data?.idEmpresario,
            idEmpresarioNatural: data?.idEmpresarioNatural,
            idUsuario: data?.idUsuario,
            idTipoDocIdentidad: payload.idTipoDocIdentidad,
            numeroDocumento: payload.numeroDocumento,
          },
          'natural',
          {
            primerNombre: String(payload.primerNombre ?? ''),
            primerApellido: String(payload.primerApellido ?? ''),
          }
        );
        return data;
      })
    );
  }

  registrarJuridica(form: HomeCuentaJuridicaFormValue, tratamientoDatos: boolean): Observable<any> {
    this.utmTracking.registrarInicioRegistro();
    const payload = this.buildJuridicaPayload(form, tratamientoDatos);
    return this.microempresario.savePersonaJuridica(payload).pipe(
      map((data: any) => {
        this.assertRegistroExitoso(data);
        this.campania.clearExtras();
        this.utmTracking.registrarRegistroExitoso(
          {
            numeroDocumento: String(payload.numeroDocumentoP ?? payload.numeroDocumento ?? ''),
            correoElectronico: String(payload.correoElectronico ?? ''),
            razonSocial: String(payload.razonSocial ?? payload.nit ?? ''),
          },
          Number(data?.idUsuario) || undefined
        );
        this.guardarSesionYNavegarOtp(
          data,
          String(payload.correoElectronico),
          {
            idEmpresario: data?.idEmpresario,
            idUsuario: data?.idUsuario,
            idPersonaEmpJuridico: data?.idPersonaEmpJuridico,
            idTipoDocIdentidad: payload.idTipoDocIdentidad,
            // Login micro usa documento del representante, no el NIT.
            numeroDocumento: payload.numeroDocumentoP,
          },
          'juridica',
          {
            primerNombre: String(payload.primerNombre ?? ''),
            primerApellido: String(payload.primerApellido ?? ''),
          }
        );
        return data;
      })
    );
  }

  private buildNaturalPayload(form: HomeCuentaNaturalFormValue, tratamientoDatos: boolean): Record<string, unknown> {
    const extras = this.campania.peekExtrasNatural() ?? {};
    const payload: Record<string, unknown> = {
      ...this.buildCorePayload(form, tratamientoDatos),
    };

    // Campos de campaña no visibles en Home pero requeridos al persistir.
    if (extras.idGenero) {
      payload.idGenero = extras.idGenero;
    }
    if (extras.idDepartamento != null) {
      payload.idDepartamento = extras.idDepartamento;
    }
    if (extras.idCiudad != null) {
      payload.idCiudad = extras.idCiudad;
    }
    if (extras.codigoCiiu) {
      payload.codigoCiiu = extras.codigoCiiu;
    }
    if (extras.valorVentas != null) {
      payload.valorVentas = extras.valorVentas;
    }
    // No se muestra en el form Home: se envía año anterior para precargar en actualizar datos.
    payload.anioVentas = extras.anioVentas || String(new Date().getFullYear() - 1);
    payload.tieneRUT = form.tieneRUT || extras.tieneRUT;

    return payload;
  }

  private normalizeTiposContacto(
    contacto: string | number | Array<string | number> | null | undefined
  ): number[] {
    let raw: Array<string | number> = [];
    if (Array.isArray(contacto)) {
      raw = contacto;
    } else if (contacto != null && contacto !== '') {
      raw = [contacto];
    }
    return raw.map(Number).filter((n) => !Number.isNaN(n) && n > 0);
  }

  private buildJuridicaPayload(form: HomeCuentaJuridicaFormValue, tratamientoDatos: boolean): Record<string, unknown> {
    const nit = String(form.nit ?? '').replace(/\D/g, '').slice(0, 9);
    // DV siempre derivado del NIT (módulo 11 DIAN), igual que actualizar-datos / registro legacy
    const digitoVerificacion =
      nit.length > 0 ? this.calcularDigitoVerificacionNIT(nit) : null;
    const extras = this.campania.peekExtrasJuridica() ?? {};

    const personaDeContacto = form.personaDeContacto?.trim() || null;
    const esOtros = personaDeContacto === 'Otros';

    // Núcleo sin extras PN (evita mezclar peekExtrasNatural en PJ).
    const payload: Record<string, unknown> = {
      ...this.buildCorePayload(form, tratamientoDatos),
      numeroDocumento: nit,
      numeroDocumentoP: String(form.numDoc ?? '').trim().toLocaleUpperCase(),
      digitoVerificacion,
      personaDeContacto,
      tipoPersonaOtro: esOtros ? form.tipoPersonaOtro?.trim() || null : null,
    };

    if (extras.idGenero) {
      payload.idGenero = extras.idGenero;
    }
    if (extras.idDepartamento != null) {
      payload.idDepartamento = extras.idDepartamento;
    }
    if (extras.idCiudad != null) {
      payload.idCiudad = extras.idCiudad;
    }
    if (extras.codigoCiiu) {
      payload.codigoCiiu = extras.codigoCiiu;
    }
    if (extras.valorVentas != null) {
      payload.valorVentas = extras.valorVentas;
    }
    // No se muestra en el form Home: se envía año anterior para precargar en actualizar datos.
    payload.anioVentas = extras.anioVentas || String(new Date().getFullYear() - 1);
    if (extras.razonSocial) {
      payload.razonSocial = extras.razonSocial;
    }

    return payload;
  }

  private buildCorePayload(
    form: HomeCuentaFormValueBase,
    tratamientoDatos: boolean
  ): Record<string, unknown> {
    return {
      primerNombre: form.primerNombre?.trim(),
      segundoNombre: form.segundoNombre?.trim() || null,
      primerApellido: form.primerApellido?.trim(),
      segundoApellido: form.segundoApellido?.trim() || null,
      numeroCelular: String(form.celular ?? '').replace(/\D/g, ''),
      correoElectronico: form.correo?.toLocaleLowerCase().trim(),
      idTipoDocIdentidad: Number(form.tipoDoc),
      numeroDocumento: String(form.numDoc ?? '').trim().toLocaleUpperCase(),
      contrasena: form.password,
      tiposContacto: this.normalizeTiposContacto(form.contacto),
      idFuenteNeocredito: Number(form.origen),
      tratamientoDatos: Boolean(tratamientoDatos),
    };
  }

  /** Módulo 11 DIAN — mismos pesos que actualizar-datos / registro PJ legacy. */
  private calcularDigitoVerificacionNIT(nitSinDV: string): number {
    const digits = nitSinDV.padStart(9, '0').slice(-9).split('').map(Number);
    const pesos = [41, 37, 29, 23, 19, 17, 13, 7, 3];
    const sum = digits.reduce((s, d, i) => s + d * pesos[i], 0);
    const remainder = sum % 11;
    return remainder <= 1 ? remainder : 11 - remainder;
  }

  private assertRegistroExitoso(data: any): void {
    if (!(data?.exitoso === true || data?.exitoso === 'true')) {
      throw { error: data?.error || 'No fue posible completar el registro' };
    }
  }

  private guardarSesionYNavegarOtp(
    data: any,
    email: string,
    preRegistro: Record<string, unknown>,
    tipoPersona: 'natural' | 'juridica',
    nombres: { primerNombre: string; primerApellido: string }
  ): void {
    this.verificacion.saveParameters({
      token: data.token,
      password: data.password,
      idUsuario: data.idUsuario,
      email,
      tipoPersona,
      primerNombre: nombres.primerNombre,
      primerApellido: nombres.primerApellido,
    });
    sessionStorage.setItem(
      'pre-registro',
      JSON.stringify({
        ...preRegistro,
        tipoPersona,
        primerNombre: nombres.primerNombre,
        primerApellido: nombres.primerApellido,
      })
    );
    this.router.navigateByUrl('/verificacion');
  }
}
